import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';

export const VIEW_MODES = ['number', 'bar', 'horizontalBar', 'pie', 'gauge', 'circle', 'list'];

export const MODE_LABELS = {
  number: 'Number',
  bar: 'Bar',
  horizontalBar: 'Horizontal',
  pie: 'Pie',
  gauge: 'Gauge',
  circle: 'Circle',
  list: 'List',
};

export function nextMode(current) {
  const i = VIEW_MODES.indexOf(current);
  return VIEW_MODES[(i + 1) % VIEW_MODES.length];
}

export function sumValues(series) {
  return series.reduce((a, b) => a + (Number(b.value) || 0), 0);
}

export function ChartNumber({ value, subtitle, theme, styles }) {
  return (
    <View style={styles.chartNumberWrap}>
      <Text style={[styles.chartNumberBig, { color: theme.colors.heading }]}>{value}</Text>
      {subtitle ? (
        <Text style={[styles.chartNumberSub, { color: theme.colors.text, opacity: 0.75 }]}>
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}

export function ChartBars({ series, horizontal, theme, styles }) {
  const max = Math.max(1, ...series.map((s) => Number(s.value) || 0));
  const colors = ['#d4af37', '#6b9bd1', '#7cb87c', '#c97b9e', '#a8a8a8', '#e6a23c'];
  if (!series.length) {
    return (
      <Text style={[styles.emptyChart, { color: theme.colors.text }]}>
        No training logged for this exercise yet.
      </Text>
    );
  }
  if (horizontal) {
    return (
      <View style={styles.hBarWrap}>
        {series.map((s, idx) => {
          const v = Number(s.value) || 0;
          const pct = Math.round((v / max) * 100);
          return (
            <View key={`${s.label}-${idx}`} style={styles.hBarRow}>
              <Text style={[styles.hBarLabel, { color: theme.colors.text }]} numberOfLines={1}>
                {s.label}
              </Text>
              <View style={[styles.hBarTrack, { backgroundColor: theme.colors.border }]}>
                <View
                  style={[
                    styles.hBarFill,
                    { width: `${pct}%`, backgroundColor: colors[idx % colors.length] },
                  ]}
                />
              </View>
              <Text style={[styles.hBarVal, { color: theme.colors.heading }]}>{v}</Text>
            </View>
          );
        })}
      </View>
    );
  }
  return (
    <View style={styles.vBarWrap}>
      {series.map((s, idx) => {
        const v = Number(s.value) || 0;
        const pct = Math.round((v / max) * 100);
        return (
          <View key={`${s.label}-${idx}`} style={styles.vBarCol}>
            <Text style={[styles.vBarVal, { color: theme.colors.heading }]}>{v}</Text>
            <View style={[styles.vBarTrack, { backgroundColor: theme.colors.border }]}>
              <View
                style={[
                  styles.vBarFill,
                  { height: `${pct}%`, backgroundColor: colors[idx % colors.length] },
                ]}
              />
            </View>
            <Text style={[styles.vBarLabel, { color: theme.colors.text }]} numberOfLines={2}>
              {s.label}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

export function ChartPie({ series, theme, styles }) {
  const total = sumValues(series);
  const colors = ['#d4af37', '#6b9bd1', '#7cb87c', '#c97b9e', '#a8a8a8', '#e6a23c'];
  if (total <= 0) {
    return (
      <Text style={[styles.emptyChart, { color: theme.colors.text }]}>
        No training logged for this exercise yet.
      </Text>
    );
  }
  return (
    <View style={styles.pieRow}>
      {series.map((s, idx) => {
        const v = Number(s.value) || 0;
        if (v <= 0) return null;
        const flex = v / total;
        return (
          <View
            key={`${s.label}-${idx}`}
            style={[styles.pieSeg, { flex, backgroundColor: colors[idx % colors.length] }]}
          />
        );
      })}
    </View>
  );
}

export function ChartGauge({ value, max, label, theme, styles }) {
  const m = Math.max(1, max || 1);
  const v = Number(value) || 0;
  const fillPct = Math.min(100, Math.round((v / m) * 100));
  return (
    <View style={styles.gaugeWrap}>
      <Text style={[styles.gaugeLabel, { color: theme.colors.text }]}>{label}</Text>
      <View style={[styles.gaugeTrack, { backgroundColor: theme.colors.border }]}>
        <View
          style={[styles.gaugeFill, { width: `${fillPct}%`, backgroundColor: theme.colors.heading }]}
        />
      </View>
      <Text style={[styles.gaugePct, { color: theme.colors.heading }]}>
        {fillPct}% of scale ({v} / {m})
      </Text>
    </View>
  );
}

export function ChartCircle({ value, max, label, theme, styles }) {
  const v = Number(value) || 0;
  const m = Math.max(v, max || 10, 1);
  const fillPct = Math.min(100, Math.round((v / m) * 100));
  const ringColor = theme.colors.primary || '#d4af37';
  return (
    <View style={styles.circleWrap}>
      <View
        style={[
          styles.circleRing,
          {
            borderColor: theme.colors.border,
            backgroundColor: theme.isDarkMode ? '#111' : '#f5f5f5',
          },
        ]}
      >
        <View
          style={[
            styles.circleArc,
            {
              borderTopColor: fillPct > 0 ? ringColor : 'transparent',
              borderRightColor: fillPct > 25 ? ringColor : 'transparent',
              borderBottomColor: fillPct > 50 ? ringColor : 'transparent',
              borderLeftColor: fillPct > 75 ? ringColor : 'transparent',
            },
          ]}
        />
        <Text style={[styles.circleValue, { color: theme.colors.heading }]}>{v}</Text>
      </View>
      {label ? (
        <Text style={[styles.circleLabel, { color: theme.colors.text, opacity: 0.8 }]}>
          {label} · {fillPct}%
        </Text>
      ) : null}
    </View>
  );
}

export function ChartList({ lines, theme, styles }) {
  if (!lines.length) {
    return (
      <Text style={[styles.emptyChart, { color: theme.colors.text }]}>No rows yet.</Text>
    );
  }
  return (
    <View style={styles.listWrap}>
      {lines.map((line, i) => (
        <Text key={i} style={[styles.listLine, { color: theme.colors.text }]}>
          {line}
        </Text>
      ))}
    </View>
  );
}

export function ReportWidget({
  title,
  mode,
  onCycleMode,
  theme,
  styles,
  children,
  containerStyle,
}) {
  return (
    <View
      style={[
        styles.widgetCard,
        {
          borderColor: theme.isDarkMode ? 'rgba(255,255,255,0.2)' : '#ccc',
          backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fafafa',
        },
        containerStyle,
      ]}
      {...(Platform.OS === 'web' ? { dataSet: { reportingWidgetCard: 'true' } } : {})}
    >
      <Text style={[styles.widgetTitle, { color: theme.colors.heading }]}>{title}</Text>
      <View style={styles.widgetBody}>{children}</View>
      <TouchableOpacity
        style={styles.widgetModeBtn}
        onPress={onCycleMode}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        {...(Platform.OS === 'web' ? { dataSet: { reportingWidgetModeBtn: 'true' } } : {})}
      >
        <Text style={[styles.widgetModeBtnText, { color: theme.colors.text }]}>
          {MODE_LABELS[mode] || mode} ↻
        </Text>
      </TouchableOpacity>
    </View>
  );
}

export function layoutDashboardWidgets(nodes, isTwoColumn, styles) {
  if (!isTwoColumn) return nodes;
  const rows = [];
  for (let i = 0; i < nodes.length; i += 2) {
    rows.push(
      <View key={`widget-row-${i}`} style={styles.widgetRow}>
        <View style={styles.widgetHalf}>{nodes[i]}</View>
        {i + 1 < nodes.length ? (
          <View style={styles.widgetHalf}>{nodes[i + 1]}</View>
        ) : (
          <View style={styles.widgetHalf} />
        )}
      </View>
    );
  }
  return rows;
}

export function renderReportWidgetBody(
  mode,
  { series, numberValue, numberSubtitle, listLines, gaugeMax },
  theme,
  styles
) {
  if (mode === 'number') {
    return (
      <ChartNumber value={numberValue} subtitle={numberSubtitle} theme={theme} styles={styles} />
    );
  }
  if (mode === 'bar') {
    return <ChartBars series={series} horizontal={false} theme={theme} styles={styles} />;
  }
  if (mode === 'horizontalBar') {
    return <ChartBars series={series} horizontal theme={theme} styles={styles} />;
  }
  if (mode === 'pie') {
    return <ChartPie series={series} theme={theme} styles={styles} />;
  }
  if (mode === 'gauge') {
    return (
      <ChartGauge
        value={numberValue}
        max={gaugeMax}
        label={numberSubtitle || ''}
        theme={theme}
        styles={styles}
      />
    );
  }
  if (mode === 'circle') {
    return (
      <ChartCircle
        value={numberValue}
        max={gaugeMax}
        label={numberSubtitle || ''}
        theme={theme}
        styles={styles}
      />
    );
  }
  return <ChartList lines={listLines} theme={theme} styles={styles} />;
}

export function getReportingWidgetStyles(theme) {
  return StyleSheet.create({
    widgetRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: 12,
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    widgetHalf: {
      flex: 1,
      minWidth: 0,
      backgroundColor: 'transparent',
    },
    widgetCard: {
      borderWidth: 1,
      borderRadius: 10,
      padding: 14,
      paddingBottom: 40,
      marginBottom: 16,
      position: 'relative',
    },
    widgetCardGrid: {
      marginBottom: 0,
      flex: 1,
      alignSelf: 'stretch',
    },
    widgetTitle: {
      fontSize: 15,
      fontWeight: '700',
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    widgetBody: { minHeight: 100, backgroundColor: 'transparent' },
    widgetModeBtn: {
      position: 'absolute',
      right: 10,
      bottom: 8,
      paddingVertical: 4,
      paddingHorizontal: 8,
      backgroundColor: 'transparent',
    },
    widgetModeBtnText: { fontSize: 12, fontWeight: '600', backgroundColor: 'transparent' },
    chartNumberWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 12,
      backgroundColor: 'transparent',
    },
    chartNumberBig: { fontSize: 44, fontWeight: '800', backgroundColor: 'transparent' },
    chartNumberSub: {
      fontSize: 13,
      marginTop: 6,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    emptyChart: { padding: 12, fontSize: 14, backgroundColor: 'transparent' },
    vBarWrap: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-around',
      height: 160,
      paddingTop: 8,
      backgroundColor: 'transparent',
    },
    vBarCol: { flex: 1, alignItems: 'center', marginHorizontal: 2, backgroundColor: 'transparent' },
    vBarVal: { fontSize: 11, marginBottom: 4, backgroundColor: 'transparent' },
    vBarTrack: {
      width: '70%',
      height: 100,
      borderRadius: 4,
      justifyContent: 'flex-end',
      overflow: 'hidden',
    },
    vBarFill: { width: '100%', minHeight: 4, borderRadius: 4 },
    vBarLabel: {
      fontSize: 10,
      marginTop: 6,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    hBarWrap: { gap: 8, backgroundColor: 'transparent' },
    hBarRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    hBarLabel: { width: 100, fontSize: 12, backgroundColor: 'transparent' },
    hBarTrack: { flex: 1, height: 14, borderRadius: 7, overflow: 'hidden', marginHorizontal: 8 },
    hBarFill: { height: '100%', borderRadius: 7 },
    hBarVal: { width: 36, fontSize: 12, textAlign: 'right', backgroundColor: 'transparent' },
    pieRow: {
      flexDirection: 'row',
      height: 48,
      borderRadius: 8,
      overflow: 'hidden',
      marginTop: 8,
    },
    pieSeg: { minWidth: 4 },
    gaugeWrap: { paddingVertical: 8, backgroundColor: 'transparent' },
    gaugeLabel: { fontSize: 13, marginBottom: 6, backgroundColor: 'transparent' },
    gaugeTrack: { height: 16, borderRadius: 8, overflow: 'hidden' },
    gaugeFill: { height: '100%', borderRadius: 8 },
    gaugePct: { marginTop: 6, fontSize: 12, backgroundColor: 'transparent' },
    circleWrap: {
      alignItems: 'center',
      paddingVertical: 8,
      backgroundColor: 'transparent',
    },
    circleRing: {
      width: 96,
      height: 96,
      borderRadius: 48,
      borderWidth: 10,
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    circleArc: {
      position: 'absolute',
      width: 96,
      height: 96,
      borderRadius: 48,
      borderWidth: 10,
      transform: [{ rotate: '-45deg' }],
    },
    circleValue: {
      fontSize: 28,
      fontWeight: '800',
      backgroundColor: 'transparent',
    },
    circleLabel: {
      marginTop: 8,
      fontSize: 12,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    listWrap: { paddingVertical: 4, backgroundColor: 'transparent' },
    listLine: {
      fontSize: 14,
      marginBottom: 6,
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    focusWidgetsSection: {
      width: '100%',
      marginBottom: 16,
      backgroundColor: 'transparent',
    },
    focusWidgetsHeading: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
  });
}
